import { FC, useEffect, useRef } from "react";
import {
    Box,
    CircularProgress,
    Typography,
    Stack,
} from "@mui/material";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import {
    fetchWalletListThunk,
    fetchWalletTransactionsThunk,
} from "../../../store/thunk/walletThunk";
import SampleWidget from "../../dashboard/widgets/GeneralInfo/SampleWidget";
import { useTranslation } from "react-i18next";
import { useWalletTransactions } from "../../../store/selectors/walletSelector";
import { ModuleListContainer, ItemCard } from "./EmployeePageWidgets.styles";

interface WalletWidgetProps {
    walletId?: string | null;
}

const WalletWidget: FC<WalletWidgetProps> = ({ walletId }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const transactions = useWalletTransactions();
    const listLoadRef = useRef(false);
    const initialLoadDone = useRef(false);

    useEffect(() => {
        if (!listLoadRef.current) {
            dispatch(fetchWalletListThunk());
            listLoadRef.current = true;
        }
    }, [dispatch]);

    useEffect(() => {
        if (walletId && !initialLoadDone.current) {
            dispatch(fetchWalletTransactionsThunk({ walletId, offset: 0, limit: 10 }));
            initialLoadDone.current = true;
        }
    }, [walletId, dispatch]);

    return (
        <Box flex={1} minWidth="300px">
            <SampleWidget title={t("wallet.title")}>
                <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("wallet.transactions.lastTransactions")}
                    </Typography>

                    {!transactions ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : transactions.items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {t("wallet.transactions.noTransactions")}
                        </Typography>
                    ) : (
                        <ModuleListContainer>
                            {transactions.items.map((tx) => (
                                <ItemCard key={tx.id}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{ mb: 0.5 }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(tx.transactionTimestamp).toLocaleDateString()}
                                        </Typography>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: tx.amount >= 0 ? "success.main" : "error.main",
                                            }}
                                        >
                                            {tx.amount > 0 ? "+" : ""}
                                            {tx.amount.toLocaleString()} {tx.currency}
                                        </Typography>
                                    </Stack>

                                    {tx.comment && (
                                        <Typography variant="body2" color="text.secondary">
                                            {tx.comment}
                                        </Typography>
                                    )}
                                </ItemCard>
                            ))}
                        </ModuleListContainer>
                    )}
                </>
            </SampleWidget>
        </Box>
    );
};

export default WalletWidget;
